import React, {
  useState, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Dropzone from 'react-dropzone';
import { format, subDays } from 'date-fns';
import * as XLSX from 'xlsx';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import Input from '../../../../shared/_TBComponents/Input';
import Modal from '../../../../shared/_TBComponents/Modal';
import Text from '../../../../shared/_TBComponents/Text';
import Toggle from '../../../../shared/_TBComponents/Toggle';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import { complexPasswordRegex, passwordRequirements } from '../../../../shared/utils/registrationSchemaHelper';
import { showSuccessToast } from '../../../../shared/utils/toastManager';
import { uploadUsers } from '../../ducks/navigatorActions';

const ModalWrapper = styled.div`
  width: min(680px, 100vw);
  min-width: min(500px, 100vw);
  display: flex;
  flex-direction: column;
  border-radius: 8px;

  @media screen and (max-width: 767px) {
    width: 100vw;
    min-width: unset;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0 0 16px;
  flex-shrink: 0;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 24px 24px;
  overflow: visible;
`;

const DateRangeRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const DatePickerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DateOffsetContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 8px;
`;

const InfoBanner = styled.div`
  background: #F5F5F5;
  border-radius: 6px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropZone = styled.div`
  border: 2px dashed ${({ isDragActive }) => (isDragActive ? '#77A7FF' : '#E0E0E0')};
  border-radius: 6px;
  padding: 24px 16px;
  background: ${({ isDragActive }) => (isDragActive ? 'rgba(119, 167, 255, 0.06)' : '#FAFAFA')};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: #77A7FF;
    background: rgba(119, 167, 255, 0.04);
  }
`;

const SelectedFileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #EEEEEE;
  flex-shrink: 0;

  .spinner-container {
    min-height: auto !important;
  }
`;

const MappingTableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid #E0E0E0;
  border-radius: 6px;
`;

const MappingTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: 'Nunito Sans';
  font-size: 13px;

  th {
    padding: 10px 12px;
    background: #F5F5F5;
    border-bottom: 1px solid #E0E0E0;
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 8px 12px;
    border-bottom: 1px solid #F0F0F0;
    color: #444444;
    white-space: nowrap;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const MappingSelect = styled.select`
  font-family: 'Nunito Sans';
  font-size: 13px;
  border: 1px solid #E0E0E0;
  border-radius: 4px;
  padding: 4px 8px;
  width: 100%;
  min-width: 140px;
  background: #fff;
  cursor: pointer;
  outline: none;
  margin-top: 6px;

  &:focus { border-color: #77A7FF; }
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ErrorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// Read the uploaded CSV or Excel file into a workbook we can validate and preview
const readWorkbook = (f) => new Promise((resolve, reject) => {
  const ext = f.name.split('.').pop().toLowerCase();
  const reader = new FileReader();
  reader.onerror = reject;
  if (ext === 'csv') {
    reader.onload = (e) => resolve(XLSX.read(e.target.result, { type: 'string' }));
    reader.readAsText(f);
  } else {
    reader.onload = (e) => resolve(XLSX.read(new Uint8Array(e.target.result), { type: 'array' }));
    reader.readAsArrayBuffer(f);
  }
});

// Guess which upload field a file column should map to based on its header name
const autoDetectField = (header, usernamesEnabled) => {
  const h = String(header).toLowerCase().trim();
  if (h.includes('first')) return 'firstName';
  if (h.includes('last')) return 'lastName';
  if (usernamesEnabled && (h.includes('user') || h.includes('login'))) return 'username';
  if (!usernamesEnabled && h.includes('email')) return 'email';
  if (h.includes('phone') || h.includes('mobile') || h.includes('tel')) return 'phone';
  if (h.includes('sso')) return 'ssoId';
  if (h.includes('external')) return 'externalId';
  return null;
};

const MAX_UPLOAD_ROWS = 1500;

const INITIAL_STATE = {
  file: null,
  selectedGroups: [],
  selectedCalendar: null,
  startDate: null,
  endDate: null,
  offsetEnabled: false,
  offsetDate: null,
  customPassword: '',
  isLoading: false,
  formError: '',
  fileError: '',
  passwordError: '',
  groupError: '',
  showPassword: true,
  fileColumns: [],
  previewRows: [],
  totalRows: 0,
  columnMappings: {},
  results: null,
};

const UploadUsersModal = ({ isOpen, onClose, onDone }) => {
  const dispatch = useDispatch();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const calendars = useSelector((state) => state.navigator.data.calendars || []);
  const groups = useSelector((state) => state.navigator.data.groups || []);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );
  const coachHasGroupRestrictions = !currentUser?.primaryAdmin && !currentUser?.fullUserAccess;

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [offsetEnabled, setOffsetEnabled] = useState(false);
  const [offsetDate, setOffsetDate] = useState(null);
  const [customPassword, setCustomPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [fileError, setFileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [groupError, setGroupError] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [fileColumns, setFileColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [columnMappings, setColumnMappings] = useState({});
  const [results, setResults] = useState(null);

  const reset = useCallback(() => {
    setStep(1);
    setFile(INITIAL_STATE.file);
    setSelectedGroups(
      coachHasGroupRestrictions && groups.length === 1
        ? [{ id: groups[0].id, name: groups[0].name }]
        : INITIAL_STATE.selectedGroups,
    );
    setSelectedCalendar(INITIAL_STATE.selectedCalendar);
    setStartDate(INITIAL_STATE.startDate);
    setEndDate(INITIAL_STATE.endDate);
    setOffsetEnabled(INITIAL_STATE.offsetEnabled);
    setOffsetDate(INITIAL_STATE.offsetDate);
    setCustomPassword(INITIAL_STATE.customPassword);
    setIsLoading(INITIAL_STATE.isLoading);
    setFormError(INITIAL_STATE.formError);
    setFileError(INITIAL_STATE.fileError);
    setPasswordError(INITIAL_STATE.passwordError);
    setGroupError(INITIAL_STATE.groupError);
    setShowPassword(INITIAL_STATE.showPassword);
    setFileColumns(INITIAL_STATE.fileColumns);
    setPreviewRows(INITIAL_STATE.previewRows);
    setTotalRows(INITIAL_STATE.totalRows);
    setColumnMappings(INITIAL_STATE.columnMappings);
    setResults(INITIAL_STATE.results);
  }, [coachHasGroupRestrictions, groups]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles?.length) {
      setFileError('Invalid file type. Only .xls, .xlsx, and .csv files are accepted.');
      return;
    }
    const f = acceptedFiles[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setFileError('File size must be under 5MB.');
      setFile(null);
      return;
    }
    setFileError('');
    setFile(f);
  };

  const validateUploadForm = () => {
    let valid = true;
    if (!file) {
      setFileError('Please select a file to upload.');
      valid = false;
    } else {
      setFileError('');
    }
    if (coachHasGroupRestrictions && selectedGroups.length === 0) {
      setGroupError('You must assign uploaded athletes to at least one group.');
      valid = false;
    } else {
      setGroupError('');
    }
    if (usernamesEnabled && !customPassword) {
      setPasswordError('A password is required when uploading users with usernames.');
      valid = false;
    } else if (customPassword && !complexPasswordRegex.test(customPassword)) {
      setPasswordError(passwordRequirements);
      valid = false;
    } else {
      setPasswordError('');
    }
    return valid;
  };

  // Reads upload file and builds the header mappings and preview rows
  const buildUploadPreview = async (f, withUsernames) => {
    const workbook = await readWorkbook(f);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

    if (rows.length < 2) {
      const err = new Error('File must contain at least one row of data after the header.');
      err.isValidationError = true;
      throw err;
    }
    if (rows.length - 1 > MAX_UPLOAD_ROWS) {
      const err = new Error(`Your file has ${rows.length - 1} rows. Uploads are limited to a maximum of ${MAX_UPLOAD_ROWS} users at a time.`);
      err.isValidationError = true;
      throw err;
    }

    const names = rows[0].map(String);
    const columns = names.map((name, i) => ({ key: `col-${i}`, name }));
    const preview = rows.slice(1, 4).map((row, ri) => ({
      id: `preview-row-${ri}`,
      cells: names.map((_, i) => String(row[i] ?? '')),
    }));
    const initialMappings = Object.fromEntries(
      columns.map(({ key, name }) => [key, autoDetectField(name, withUsernames)]),
    );

    return {
      columns, preview, initialMappings, totalRows: rows.length - 1,
    };
  };

  // Validate upload setup then reads the file and moves to column mapping step
  const handleVerify = async () => {
    if (!validateUploadForm()) return;

    setFormError('');

    try {
      const {
        columns,
        preview, initialMappings, totalRows: parsedTotalRows,
      } = await buildUploadPreview(file, usernamesEnabled);
      setFileColumns(columns);
      setPreviewRows(preview);
      setTotalRows(parsedTotalRows);
      setColumnMappings(initialMappings);
      setStep(2);
    } catch (err) {
      setFormError(err?.isValidationError
        ? err.message
        : 'Could not read file. Please ensure it is a valid Excel or CSV file.');
    }
  };

  // Makes sure required fields are mapped once before uploading the file
  const validateMappings = (overrideMappings) => {
    const mappings = overrideMappings ?? columnMappings;
    const mappedFields = Object.values(mappings).filter(Boolean);
    const hasDuplicates = mappedFields.length !== new Set(mappedFields).size;
    if (hasDuplicates) return 'Each field can only be assigned to one column.';

    const identifierField = usernamesEnabled ? 'username' : 'email';
    const required = ['firstName', 'lastName', identifierField];
    const missing = required.filter((f) => !mappedFields.includes(f));
    if (missing.length) {
      const label = usernamesEnabled ? 'Username' : 'Email Address';
      return `You must map First Name, Last Name, and ${label}.`;
    }

    const hasRequiredOnBlankColumn = fileColumns
      .filter(({ name }) => !name.trim())
      .some(({ key }) => required.includes(mappings[key]));
    if (hasRequiredOnBlankColumn) {
      return 'One or more required fields is mapped to a column with no header. Please add column headers to your file and re-upload.';
    }

    return null;
  };

  // Builds the FormData object for the upload request
  const buildUploadFormData = () => {
    const formData = new FormData();
    formData.append('file', file);
    const namedMappings = Object.fromEntries(
      fileColumns.map(({ key, name }) => [name, columnMappings[key]]),
    );
    formData.append('changedHeaders', JSON.stringify(namedMappings));
    if (selectedGroups.length) {
      formData.append('groupIds', JSON.stringify(selectedGroups.map((g) => g.id)));
    }
    if (selectedCalendar) formData.append('calendarId', selectedCalendar.id);
    if (startDate) formData.append('startDate', format(startDate, 'yyyy-MM-dd'));
    if (endDate) formData.append('endDate', format(endDate, 'yyyy-MM-dd'));
    if (offsetEnabled && offsetDate) {
      formData.append('offsetDate', format(offsetDate, 'yyyy-MM-dd'));
    }
    if (customPassword) formData.append('password', customPassword);
    return formData;
  };

  const isPartialUploadResult = (data) => (
    data && data.added != null && data.errors != null
  );

  // Finalizes upload process by validating mappings and dispatching upload action
  const handleFinalize = async () => {
    const mappingError = validateMappings();
    if (mappingError) { setFormError(mappingError); return; }

    setFormError('');
    setIsLoading(true);

    try {
      const result = await dispatch(uploadUsers(accountCode, buildUploadFormData()));
      if (result?.errors?.length) {
        setResults(result);
        setStep(3);
      } else {
        showSuccessToast(`${result?.added ?? 0} user${result?.added !== 1 ? 's' : ''} successfully uploaded!`);
        reset();
        if (onDone) onDone(); else onClose();
      }
    } catch (err) {
      const data = err?.response?.data;
      if (isPartialUploadResult(data)) {
        setResults(data);
        setStep(3);
      } else {
        setFormError(
          data?.message
          || err?.response?.headers?.message
          || 'An error occurred. Please try again.',
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const identifierLabel = usernamesEnabled ? 'Username' : 'Email Address';
  const identifierField = usernamesEnabled ? 'username' : 'email';
  const templateHref = usernamesEnabled
    ? '/downloads/TeamBuildr_Upload_Template_Usernames.xlsx'
    : '/downloads/TeamBuildr_Upload_Template.xlsx';

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#77A7FF' : '#E0E0E0',
      borderWidth: '1px',
      boxShadow: 'none',
      '&:hover': { borderColor: state.isFocused ? '#77A7FF' : '#E0E0E0' },
    }),
  };

  const fieldOptions = [
    { value: 'firstName', label: 'First Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: identifierField, label: identifierLabel },
    { value: 'phone', label: 'Phone Number' },
    { value: 'ssoId', label: 'SSO ID' },
    { value: 'externalId', label: 'External ID' },
    { value: null, label: 'Skip Column' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      customBorderRadius='8px'
      customPadding='0px'
      customMaxWidth='680px'
      customMinWidth='min(500px, 100vw)'
      customButton
    >
      <ModalWrapper>
        <Header>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 700 }}>
            Upload Users
          </Text>
          <Button
            icon='remove'
            iconSize={15}
            onClick={handleClose}
            fontSize='13px'
            txtColor='#9E9E9E'
            noBorder
          />
        </Header>

        {step === 1 && (
          <>
            <Body>
              <InfoBanner>
                <Text styledTextProps={{ fontSize: '14px', color: '#444444' }}>
                  Upload file
                  {' '}
                  <strong>must include</strong>
                  {' '}
                  First Name, Last Name and
                  {' '}
                  {identifierLabel}
                  .
                </Text>
                <Text styledTextProps={{ fontSize: '14px', color: '#444444' }}>
                  Only
                  {' '}
                  <strong>Excel (.xls and .xlsx) and CSV</strong>
                  {' '}
                  files are currently accepted.
                </Text>
                <Text styledTextProps={{ fontSize: '14px', color: '#444444' }}>
                  Uploads are limited to a maximum of
                  {' '}
                  <strong>
                    {MAX_UPLOAD_ROWS}
                    {' '}
                    users
                  </strong>
                  {' '}
                  at a time.
                </Text>
                <Text styledTextProps={{ fontSize: '14px', color: '#444444' }}>
                  <a href={templateHref} download style={{ color: '#77A7FF', textDecoration: 'none' }}>Click here</a>
                  {' '}
                  to download the official upload template.
                </Text>
              </InfoBanner>

              <FieldGroup>
                <Text
                  styledTextProps={{ fontSize: '16px', fontWeight: 700, color: '#333333' }}
                  styledWrapperProps={{ marginBottom: '5px' }}
                >
                  File
                </Text>
                <Dropzone
                  onDrop={handleDrop}
                  accept='.xls,.xlsx,.csv'
                  multiple={false}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <DropZone isDragActive={isDragActive} {...getRootProps()}>
                      <input {...getInputProps()} />
                      {file ? (
                        <SelectedFileRow>
                          <Text
                            styledTextProps={{ fontSize: '13px', color: '#4CAF50', fontWeight: 600 }}
                          >
                            {file.name}
                          </Text>
                          <Button
                            icon='remove'
                            iconSize={12}
                            iconOnly
                            noBorder
                            txtColor='#9E9E9E'
                            onClick={(e) => { e.stopPropagation(); setFile(null); }}
                          />
                        </SelectedFileRow>
                      ) : (
                        <Text styledTextProps={{ fontSize: '14px', color: '#666666', textAlign: 'center' }}>
                          {isDragActive ? 'Drop file here' : (
                            <>
                              <span style={{ color: '#77A7FF', fontWeight: 700 }}>Choose a file</span>
                              {' or drag and drop it here'}
                            </>
                          )}
                        </Text>
                      )}
                    </DropZone>
                  )}
                </Dropzone>
                {fileError && (
                  <Text
                    styledTextProps={{
                      fontSize: '13px', fontWeight: 700, color: '#CC0404', margin: 0,
                    }}
                    styledWrapperProps={{ marginTop: '6px' }}
                  >
                    {fileError}
                  </Text>
                )}
              </FieldGroup>

              <FieldGroup>
                <SelectBox
                  title='Assign Group(s)'
                  isMulti
                  options={groups.map(({ id, name }) => ({ id, name }))}
                  value={selectedGroups}
                  onChangeFunction={(val) => {
                    setSelectedGroups(val || []);
                    if (groupError) setGroupError('');
                  }}
                  placeholder='Select group(s) for athlete'
                  customStyles={selectStyles}
                  titleStyledProps={{ fontWeight: 700 }}
                />
                {groupError && (
                  <Text
                    styledTextProps={{
                      fontSize: '13px', fontWeight: 700, color: '#CC0404', margin: 0,
                    }}
                    styledWrapperProps={{ marginTop: '6px' }}
                  >
                    {groupError}
                  </Text>
                )}
              </FieldGroup>

              <FieldGroup>
                <SelectBox
                  title='Assign Calendar'
                  options={calendars.map(({ id, name }) => ({ id, name }))}
                  value={selectedCalendar}
                  onChangeFunction={(opt) => {
                    setSelectedCalendar(opt || null);
                    if (opt) {
                      setStartDate((prev) => prev || new Date());
                    } else {
                      setStartDate(null);
                      setEndDate(null);
                      setOffsetEnabled(false);
                      setOffsetDate(null);
                    }
                  }}
                  placeholder='Select a Calendar'
                  isClearable
                  customStyles={selectStyles}
                  titleStyledProps={{ fontWeight: 700 }}
                />
              </FieldGroup>

              {selectedCalendar && (
                <>
                  <DateRangeRow>
                    <DatePickerField>
                      <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>Start Date</Text>
                      <DatePicker
                        singleDate
                        startDate={startDate}
                        setStartDate={(date) => {
                          setStartDate(date);
                          if (date && endDate && endDate < date) setEndDate(null);
                          if (date && offsetDate && offsetDate >= date) setOffsetDate(null);
                        }}
                        maxDate={null}
                        placeholderText='Select start date'
                        textColor='#333333'
                      />
                    </DatePickerField>
                    <DatePickerField>
                      <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>
                        End Date
                        {' '}
                        <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>
                          (Leave blank if no end date)
                        </span>
                      </Text>
                      <DatePicker
                        singleDate
                        startDate={endDate}
                        setStartDate={setEndDate}
                        minDate={startDate || undefined}
                        maxDate={null}
                        placeholderText='Select end date'
                        textColor='#333333'
                      />
                    </DatePickerField>
                  </DateRangeRow>
                  <DateOffsetContainer>
                    <ToggleContainer>
                      <Toggle
                        defaultChecked={offsetEnabled}
                        onChange={(e) => {
                          setOffsetEnabled(e.target.checked);
                          if (!e.target.checked) setOffsetDate(null);
                        }}
                        onHoverColor='#0DCC8A'
                        onColor='#0DCC8A'
                        offColor='#E0E0E0'
                        thumbColorOff
                        thumbBorderOff
                        width='auto'
                      />
                      <Text styledTextProps={{ color: '#333333' }}>
                        Enable Calendar Offset (Set Specific Date as First Workout)
                      </Text>
                    </ToggleContainer>
                    {offsetEnabled && (
                      <DatePickerField>
                        <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>
                          Offset Date
                          {' '}
                          <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>(Optional)</span>
                        </Text>
                        <DatePicker
                          singleDate
                          startDate={offsetDate}
                          setStartDate={setOffsetDate}
                          maxDate={startDate ? subDays(startDate, 1) : null}
                          placeholderText='Select offset date'
                          textColor='#333333'
                        />
                      </DatePickerField>
                    )}
                  </DateOffsetContainer>
                </>
              )}

              <FieldGroup>
                <Input
                  label={(
                    <>
                      Custom Password
                      {!usernamesEnabled && (
                        <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>
                          {' (optional)'}
                        </span>
                      )}
                    </>
                  )}
                  name='customPassword'
                  type={showPassword ? 'text' : 'password'}
                  value={customPassword}
                  onChange={(e) => {
                    setCustomPassword(e.target.value);
                    if (passwordError) setPasswordError('');
                  }}
                  onBlur={() => {
                    if (usernamesEnabled && !customPassword) {
                      setPasswordError('A password is required when uploading users with usernames.');
                    } else if (customPassword && !complexPasswordRegex.test(customPassword)) {
                      setPasswordError(passwordRequirements);
                    }
                  }}
                  placeholder='Set the same password for all users'
                  fontSize='14px'
                  height='38px'
                  width='100%'
                  error={!!passwordError}
                  errorMsg={passwordError}
                  right='5px'
                  top='38px'
                  showEyeToggle
                  onEyeToggleClick={() => setShowPassword(!showPassword)}
                  isPasswordVisible={showPassword}
                  eyeRight='0px'
                  eyeTop='33px'
                  styledPropsLabel={{ fontWeight: 700, fontSize: '16px', color: '#333333' }}
                  styledPropsInput={{ style: { border: '1px solid #EEEEEE' } }}
                />
                {!usernamesEnabled && !customPassword && (
                  <Text
                    styledTextProps={{ fontSize: '12px', color: '#9E9E9E' }}
                    styledWrapperProps={{ marginTop: '6px' }}
                  >
                    Leave blank to auto-generate passwords for each user.
                  </Text>
                )}
              </FieldGroup>

              {formError && (
                <Text
                  styledTextProps={{
                    fontSize: '13px', fontWeight: 700, color: '#CC0404', margin: 0,
                  }}
                  styledWrapperProps={{ background: 'rgba(204, 4, 4, 0.08)', borderRadius: '4px', padding: '8px 12px' }}
                >
                  {formError}
                </Text>
              )}
            </Body>

            <Footer>
              <Button
                name='Verify Upload'
                fontSize='14px'
                txtColor='#FFF'
                bgColor='#444444'
                borderColor='#444444'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                onClick={handleVerify}
              />
            </Footer>
          </>
        )}

        {step === 2 && (
          <>
            <Body>
              <Text styledTextProps={{ fontSize: '14px', color: '#444444' }}>
                Match each column from your file to the correct field.
                <br />
                {`First Name, Last Name, and ${identifierLabel} are required.`}
              </Text>
              {totalRows > previewRows.length && (
                <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>
                  {`Showing a preview of ${previewRows.length} of ${totalRows} rows in your file.`}
                </Text>
              )}
              <MappingTableWrapper>
                <MappingTable>
                  <thead>
                    <tr>
                      {fileColumns.map(({ key, name }) => (
                        <th key={key}>
                          <div style={{ minHeight: '18px' }}>{name}</div>
                          <MappingSelect
                            value={columnMappings[key] ?? ''}
                            onChange={(e) => {
                              const { value } = e.target;
                              const newMappings = { ...columnMappings, [key]: value || null };
                              setColumnMappings(newMappings);
                              if (formError) {
                                setFormError(validateMappings(newMappings) || '');
                              }
                            }}
                          >
                            {fieldOptions.map((opt) => (
                              <option key={opt.label} value={opt.value ?? ''}>
                                {opt.label}
                              </option>
                            ))}
                          </MappingSelect>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row) => (
                      <tr key={row.id}>
                        {row.cells.map((cell, ci) => (
                          <td key={fileColumns[ci].key}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </MappingTable>
              </MappingTableWrapper>

              {formError && (
                <Text
                  styledTextProps={{
                    fontSize: '13px', fontWeight: 700, color: '#CC0404', margin: 0,
                  }}
                  styledWrapperProps={{ background: 'rgba(204, 4, 4, 0.08)', borderRadius: '4px', padding: '8px 12px' }}
                >
                  {formError}
                </Text>
              )}
            </Body>

            <Footer>
              <Button
                name='Back'
                fontSize='14px'
                txtColor='#757575'
                bgColor='#FFF'
                borderColor='#EEEEEE'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                onClick={() => { setStep(1); setFormError(''); }}
              />
              <Button
                name='Finalize Upload'
                loadingText='Uploading...'
                fontSize='14px'
                txtColor='#FFF'
                bgColor='#444444'
                borderColor='#444444'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                onClick={handleFinalize}
                disabled={isLoading}
                isLoading={isLoading}
                lightSpinner
              />
            </Footer>
          </>
        )}

        {step === 3 && results && (
          <>
            <Body>
              <ResultsContainer>
                <Text styledTextProps={{ fontSize: '18px', fontWeight: 700, color: '#4CAF50' }}>
                  {`${results.added} New User${results.added !== 1 ? 's' : ''} Added`}
                </Text>
                {results.skipped > 0 && (
                  <Text styledTextProps={{ fontSize: '14px', color: '#9E9E9E' }}>
                    {`${results.skipped} row${results.skipped !== 1 ? 's' : ''} skipped`}
                  </Text>
                )}
                {results.errors?.length > 0 && (
                  <ErrorSection>
                    <Text styledTextProps={{ fontSize: '14px', fontWeight: 700, color: '#CC0404' }}>
                      {`${results.errors.length} Error${results.errors.length !== 1 ? 's' : ''} Found - Details Below:`}
                    </Text>
                    {results.errors.map((err) => (
                      <Text
                        key={err.row != null ? err.row : err.message}
                        styledTextProps={{ fontSize: '13px', fontWeight: 700, color: '#CC0404' }}
                        styledWrapperProps={{ paddingLeft: '12px' }}
                      >
                        {err.row != null ? `Row ${err.row}: ` : ''}
                        {err.message}
                      </Text>
                    ))}
                  </ErrorSection>
                )}
              </ResultsContainer>
            </Body>

            <Footer>
              <Button
                name='Try Again'
                fontSize='14px'
                txtColor='#444444'
                bgColor='#FFF'
                borderColor='#EEEEEE'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                onClick={reset}
              />
              <Button
                name='Finish'
                fontSize='14px'
                txtColor='#FFF'
                bgColor='#444444'
                borderColor='#444444'
                customBorderRadius='6px'
                styledTextProps={{ fontWeight: 600 }}
                onClick={handleClose}
              />
            </Footer>
          </>
        )}
      </ModalWrapper>
    </Modal>
  );
};

UploadUsersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

UploadUsersModal.defaultProps = {
  onDone: null,
};

export default UploadUsersModal;
